import { useState, useEffect, useCallback, useRef } from 'react';
import { getKnowledgeCategoryFiles, getKnowledgeFiles } from '../../../api/services/knowledge';

// Кэш документов (модульный уровень). Очищается при logout через clearDocumentsCache().
let documentsCache = {};
let lastFetchTime = {};
const CACHE_TIMEOUT = 2 * 60 * 1000; // 2 минуты

/** Очистка кэша документов (вызывать при logout). */
export const clearDocumentsCache = () => {
  documentsCache = {};
  lastFetchTime = {};
};

// Функция для создания ключа кэша из параметров
const getCacheKey = (params) => {
  if (!params) return 'default';
  
  // Извлекаем параметры с дефолтными значениями
  const { 
    page = 1, 
    size = 10, 
    search = '', 
    category_uid = 'all' 
  } = params;
  
  // Преобразуем параметры в числа для надежности
  const pageNum = Number(page) || 1;
  const sizeNum = Number(size) || 10;
  
  // Создаем ключ кэша
  const key = `${category_uid}:${pageNum}:${sizeNum}:${search}`;
  return key;
};

/**
 * Хук для получения списка файлов категории базы знаний
 * 
 * @param {Object} initialParams - Начальные параметры для запроса
 * @returns {Object} - Объект, содержащий список файлов, состояние загрузки и ошибку
 */
export const useFetchDocuments = (initialParams = {}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [params, setParams] = useState(initialParams);
  const [totalCount, setTotalCount] = useState(0);
  
  // Используем useRef для отслеживания монтирования компонента
  const isMounted = useRef(true);
  
  // Используем useRef для отслеживания первого рендера
  const isFirstRender = useRef(true);
  
  // Используем useRef для сохранения предыдущих параметров
  const prevParamsRef = useRef(JSON.stringify(params));
  
  // Ref для отслеживания запроса в процессе
  const requestInProgress = useRef(false);

  // Функция для обновления параметров запроса с мемоизацией
  const updateParams = useCallback((newParams) => {
    if (!newParams || typeof newParams !== 'object') {
      return;
    }
    
    // Проверяем и конвертируем числовые параметры
    const normalizedParams = { ...newParams };
    
    if (normalizedParams.page !== undefined) {
      normalizedParams.page = Number(normalizedParams.page);
      if (isNaN(normalizedParams.page)) normalizedParams.page = 1;
    }
    
    if (normalizedParams.size !== undefined) {
      normalizedParams.size = Number(normalizedParams.size);
      if (isNaN(normalizedParams.size)) normalizedParams.size = 20;
    }
    
    // Глубокое сравнение, чтобы избежать лишних обновлений
    const currentParamsString = JSON.stringify(params);
    const updatedParams = { ...params, ...normalizedParams };
    const newParamsString = JSON.stringify(updatedParams);
    
    
    if (currentParamsString !== newParamsString) {
      
      // Сбрасываем документы и totalCount при смене параметров
      // Это предотвратит отображение старых данных во время загрузки новых
      if (updatedParams.category_uid !== params.category_uid) {
        setDocuments([]);
        setTotalCount(0);
      }
      
      setParams(updatedParams);
      
      // Сразу обновляем ссылку на предыдущие параметры, чтобы избежать дублирования запросов
      prevParamsRef.current = newParamsString;
    }
  }, [params]);

  // Функция для выполнения запроса с мемоизацией
  const fetchDocuments = useCallback(async (forceRefresh = false) => {
    if (!isMounted.current) return;
    if (requestInProgress.current) {
      return;
    }
    
    const cacheKey = getCacheKey(params);
    const now = Date.now();
    
    
    // Определяем, изменилась ли страница по сравнению с предыдущим запросом
    const prevParams = prevParamsRef.current ? JSON.parse(prevParamsRef.current) : {};
    const pageChanged = prevParams.page !== undefined && 
                        params.page !== undefined && 
                        Number(prevParams.page) !== Number(params.page);
                        
    if (pageChanged) {
      forceRefresh = true;
    }
    
    // При принудительном обновлении очищаем кэш для текущего ключа
    if (forceRefresh && documentsCache[cacheKey]) {
      delete documentsCache[cacheKey];
    }
    
    // Глубокая копия параметров для безопасности
    const safeParams = JSON.parse(JSON.stringify(params));
    
    // Убеждаемся, что номер страницы и размер страницы являются числами
    if (safeParams.page !== undefined) {
      safeParams.page = Number(safeParams.page);
      if (isNaN(safeParams.page)) safeParams.page = 1;
    }
    
    if (safeParams.size !== undefined) {
      safeParams.size = Number(safeParams.size);
      if (isNaN(safeParams.size)) safeParams.size = 20;
    }
    
    // Проверяем кэш, если не требуется принудительное обновление
    if (!forceRefresh && 
        documentsCache[cacheKey] && 
        lastFetchTime[cacheKey] && 
        (now - lastFetchTime[cacheKey] < CACHE_TIMEOUT)) {
      
      const cachedData = documentsCache[cacheKey];
      setDocuments(cachedData.items);
      setTotalCount(cachedData.total);
      return;
    }
    
    requestInProgress.current = true;
    setLoading(true);
    setError(null); // Сбрасываем ошибку перед новым запросом
    
    
    try {
      // Извлекаем category_uid из параметров (используем безопасную копию)
      const { category_uid, ...queryParams } = safeParams;
      
      
      let result;
      
      // Если категория "all" или не указана, используем getKnowledgeFiles
      if (!category_uid || category_uid === 'all') {
        // Получаем все файлы
        result = await getKnowledgeFiles(queryParams);
      } else {
        // Получаем файлы для указанной категории
        result = await getKnowledgeCategoryFiles(category_uid, queryParams);
      }
      
      if (isMounted.current) {
        // Проверка валидности ответа
        if (!result || typeof result !== 'object') {
          setDocuments([]);
          setTotalCount(0);
          setError('Получен некорректный ответ от сервера');
          return;
        }
        
        
        // Безопасно устанавливаем документы, проверяя возможные форматы ответа
        let items = [];
        let total = 0;
        
        if (Array.isArray(result)) {
          // Если ответ - массив
          items = result;
          total = result.length;
        } else if (result.items && Array.isArray(result.items)) {
          // Если ответ имеет поле items
          items = result.items;
          // Используем общее количество из ответа API или размер массива
          total = typeof result.total === 'number' ? result.total : items.length;
        } else if (result.documents && Array.isArray(result.documents)) {
          // Если ответ имеет поле documents
          items = result.documents;
          // Используем общее количество из ответа API или размер массива
          total = typeof result.total === 'number' ? result.total : items.length;
        } else {
          // Ищем любое поле, содержащее массив
          const arrayFields = Object.keys(result).filter(key => Array.isArray(result[key]));
          if (arrayFields.length > 0) {
            items = result[arrayFields[0]];
            // Используем общее количество из ответа API или размер массива
            total = typeof result.total === 'number' ? result.total : items.length;
          }
        }
        
        // Если все же ничего не найдено
        if (!Array.isArray(items)) {
          setDocuments([]);
          setTotalCount(0);
          setError('Получен некорректный формат данных от сервера');
          return;
        }
        
        // Добавляем category_uid к каждому документу, если она не указана
        const itemsWithCategory = items.map(item => ({
          ...item,
          category_uid: item.category_uid || category_uid
        }));
        
        // Обновляем кэш
        documentsCache[cacheKey] = { items: itemsWithCategory, total };
        lastFetchTime[cacheKey] = now;
        
        // Обновляем состояние
        setDocuments(itemsWithCategory);
        
        // Определяем правильное общее количество документов
        let finalTotalCount = total;
        
        // Проверяем непосредственно поля в ответе
        if (typeof result.total === 'number') {
          finalTotalCount = result.total;
        } else if (typeof result.total_count === 'number') {
          finalTotalCount = result.total_count;
        } else if (typeof result.count === 'number') {
          finalTotalCount = result.count;
        }
        
        // Если количество элементов в массиве больше указанного total, используем длину массива
        if (items.length > finalTotalCount) {
          finalTotalCount = items.length;
        }
        
        setTotalCount(finalTotalCount);
        setError(null);
        
      }
    } catch (err) {
      if (isMounted.current) {
        void err;
        
        // При ошибке используем кэш, если он есть
        if (documentsCache[cacheKey]) {
          setDocuments(documentsCache[cacheKey].items);
          setTotalCount(documentsCache[cacheKey].total);
        } else {
          setDocuments([]);
          setTotalCount(0);
        }
        
        setError('Ошибка загрузки документов: ' + (err.message || 'Неизвестная ошибка'));
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      requestInProgress.current = false;
    }
  }, [params]);

  // Эффект для отслеживания монтирования компонента
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Эффект для загрузки данных при изменении параметров
  useEffect(() => {
    // Пропускаем первый рендер, чтобы избежать лишнего запроса
    if (isFirstRender.current) {
      isFirstRender.current = false;
      // При первом рендере не делаем запрос - он должен быть вызван явно через refetch
      return () => {};
    }
    
    // Проверяем, действительно ли параметры изменились, используя строковое сравнение
    const currentParamsString = JSON.stringify(params);
    
    if (prevParamsRef.current !== currentParamsString) {
      fetchDocuments();
      prevParamsRef.current = currentParamsString;
    }
  }, [params, fetchDocuments]);

  // Функция для принудительного обновления
  const forceRefetch = useCallback(() => {
    fetchDocuments(true);
  }, [fetchDocuments]);

  // Возвращаем данные и функции для взаимодействия
  return { 
    documents, 
    loading, 
    error, 
    totalCount,
    isEmpty: !loading && documents.length === 0, // Флаг пустого результата
    updateParams,
    params,
    refetch: forceRefetch,  // Функция для принудительного обновления
    resetError: () => setError(null) // Функция для сброса ошибки
  };
}; 